import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { getDrClearCareEstimate } from 'api/Appointment';

import { buildDrClearCareEstimate } from 'components/Patient/checkin/SecurePayment/components/CheckinPreviewInvoice/DrClearCare/lib/buildDrClearCareEstimate';

/**
 * Loads the persisted primary eligibility benefits for a check-in appointment
 * (/api/patient/checkin/dr_clearcare) and assembles the Dr. ClearCare estimate
 * by combining them with the already-fetched invoice (charges / allowed /
 * write-off / services). Returns `estimate: null` when there's nothing real to
 * show so the panel stays hidden — never fabricates numbers.
 */
export const useDrClearCareEstimate = ({ appointmentId, invoice, enabled = true }) => {
  const navigate = useNavigate();

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryFn: () => getDrClearCareEstimate(navigate, { appointmentId }),
    queryKey: ['dr-clearcare-estimate', appointmentId],
    refetchOnWindowFocus: false,
    staleTime: 0,
    enabled: !!appointmentId && enabled
  });

  const estimate = useMemo(
    () =>
      data?.code === 0 ? buildDrClearCareEstimate({ benefits: data.benefits, invoice }) : null,
    [data, invoice]
  );

  const isErrorState = isError || (data != null && data.code !== 0);

  return {
    estimate,
    benefits: data?.code === 0 ? (data.benefits ?? null) : null,
    isLoading,
    isFetching,
    isError: isErrorState,
    refetch
  };
};
